// Was this trip's distance actually measured, or measured across a hole? — 0199.
//
// ⚠ ITS OWN FILE, pure, for the reason `cash.custody.ts` is: the admin vitest
// setup does not resolve the app's `@/` alias, so anything importing the
// Supabase client drags in a module the test runner cannot load.
//
// ═══ WHAT 0199 MEASURES AND WHY IT MATTERS ═══
//
// `total_km` is `ST_Length(track)/1000` over whatever points reached
// `rider_locations`, and `verifiedDispatchEarning(km)` pays ₱40 + ₱3 × max(0,
// km − 5) from it. The track is written every 5 s by the PWA while it is open.
// `navHandoff.ts` deliberately sends the rider into Waze or Google Maps, our tab
// is backgrounded, iOS suspends it, and `watchPosition` stops. `ST_MakeLine`
// then joins the last point before the handoff straight to the first point
// after it — and a chord is never longer than the path it replaces, so
// `total_km` UNDER-reports and the rider is UNDER-paid, silently.
//
// `0199` records three diagnostics on `end_trip`: `tracking_gaps` (intervals
// over 60 s, twelve times the write cadence), `max_gap_seconds`, and
// `straight_line_km` (the direct start-to-end chord).
//
// ⚠ THIS FILE DIAGNOSES. IT DOES NOT REPRICE, AND MUST NOT LEARN TO.
// 0199 is explicit: "What to do about a gapped trip is an OPEN ITEM, not a
// default. Paying a road-network estimate instead of the measured track would
// make client-side conditions payable, which SECURITY §5 and money-math.md §3
// both forbid; paying the chord is what we do today and it underpays. Both are
// defensible and the choice changes rider earnings, so it belongs to Luigi."
//
// ⚠ AND THERE IS NO HONEST PESO FIGURE TO SHOW. The whole point of a gap is
// that the distance travelled through it is UNKNOWN. Any "₱ owed" number would
// require assuming a distance nobody measured — which is the invented rule 0199
// refused to make. So this surface reports distances and counts, and stops.

/** One completed trip's measurement diagnostics. */
export interface TripTracking {
  id: string;
  riderName: string | null;
  endedAt: string | null;
  /** What was measured, and what pay is computed from. */
  totalKm: number;
  /** Direct start-to-end distance. */
  straightLineKm: number | null;
  /** Intervals over 60 s. 0 on a healthy trip. */
  trackingGaps: number;
  maxGapSeconds: number;
  /** Null on any trip that ended before 0199 — not zero. See `isMeasured`. */
  measured: boolean;
}

/**
 * ⚠ ABSENT IS NOT ZERO. Every trip that ended before 0199 has NULL in all three
 * columns, and there is no backfill — the points to recompute from may have been
 * pruned, and inventing a gap count for a finished trip would be worse than
 * saying nothing. A null must never render as "0 gaps, healthy".
 */
export function isMeasured(row: {
  max_gap_seconds: number | null;
  tracking_gaps: number | null;
}): boolean {
  return row.max_gap_seconds !== null && row.tracking_gaps !== null;
}

/**
 * Track ÷ chord.
 *
 * A real ride bends around roads, so the track is meaningfully longer than the
 * straight line. A backgrounded one collapses onto its own chord and the ratio
 * converges on 1.00. 0199: "the cheapest signal that a trip was never really
 * measured, and it needs no GPS expertise to read".
 *
 * Null when there is no chord to compare against — a zero-length or
 * single-point trip divides by nothing, and 1.00 would be a lie about it.
 */
export function trackToChordRatio(trip: TripTracking): number | null {
  if (trip.straightLineKm === null || trip.straightLineKm <= 0) return null;
  return Math.round((trip.totalKm / trip.straightLineKm) * 100) / 100;
}

export type TrackingVerdict = "healthy" | "gapped_unpaid" | "gapped_free" | "not_measured";

/**
 * ⚠ THE TWO GAPPED CASES ARE NOT THE SAME PROBLEM, and 0199 says why:
 * "Below 5 km the fare is a flat ₱40 and a hole costs nobody anything. Above it,
 * every missing kilometre is ₱3 he earned and is not paid."
 *
 * So a gapped 3 km trip is a data-quality note; a gapped 12 km trip is a rider
 * who was probably underpaid. Ranking them the same buries the second in the
 * first — Cainta is mostly short trips, "which is exactly why this would go
 * unnoticed until it didn't".
 *
 * ⚠ THE 5 km BOUNDARY IS THE FARE'S, NOT A THRESHOLD CHOSEN HERE. It is
 * `verifiedDispatchEarning`'s own free allowance; if that changes, this follows
 * it rather than the other way round.
 */
export const FREE_ALLOWANCE_KM = 5;

export function trackingVerdict(trip: TripTracking): TrackingVerdict {
  if (!trip.measured) return "not_measured";
  if (trip.trackingGaps === 0) return "healthy";
  return trip.totalKm > FREE_ALLOWANCE_KM ? "gapped_unpaid" : "gapped_free";
}

export function verdictLabel(v: TrackingVerdict): string {
  switch (v) {
    case "gapped_unpaid":
      return "Gapped — above the flat fare";
    case "gapped_free":
      return "Gapped — inside the flat fare";
    case "healthy":
      return "Measured throughout";
    default:
      return "Not measured";
  }
}

export function verdictTone(v: TrackingVerdict): "warning" | "muted" | "success" {
  switch (v) {
    case "gapped_unpaid":
      return "warning";
    case "healthy":
      return "success";
    default:
      return "muted";
  }
}

export interface TrackingSummary {
  measured: number;
  notMeasured: number;
  healthy: number;
  gappedFree: number;
  gappedUnpaid: number;
  /**
   * Σ (total_km − straight_line_km) over gapped trips above the flat fare.
   *
   * ⚠ NOT "DISTANCE OWED" AND NOT CONVERTIBLE TO PESOS. It is how much of the
   * measured distance still bends — a trip whose track has collapsed onto its
   * chord contributes ~0 here, which is the point: a LOW number on a gapped
   * trip means more of the real path is missing, not less. It is a shape
   * statistic for the Open Item, never an amount.
   */
  bendKmOnUnpaidGaps: number;
}

export function summarise(trips: readonly TripTracking[]): TrackingSummary {
  const s: TrackingSummary = {
    measured: 0,
    notMeasured: 0,
    healthy: 0,
    gappedFree: 0,
    gappedUnpaid: 0,
    bendKmOnUnpaidGaps: 0,
  };
  for (const t of trips) {
    const v = trackingVerdict(t);
    if (v === "not_measured") {
      s.notMeasured += 1;
      continue;
    }
    s.measured += 1;
    if (v === "healthy") s.healthy += 1;
    if (v === "gapped_free") s.gappedFree += 1;
    if (v === "gapped_unpaid") {
      s.gappedUnpaid += 1;
      if (t.straightLineKm !== null) {
        s.bendKmOnUnpaidGaps += Math.max(0, t.totalKm - t.straightLineKm);
      }
    }
  }
  s.bendKmOnUnpaidGaps = Math.round(s.bendKmOnUnpaidGaps * 100) / 100;
  return s;
}

/**
 * Worst first — the trips most likely to have cost a rider money.
 *
 * Ordering: unpaid gaps before free ones before healthy, then by how far the
 * track has collapsed toward its chord (ratio ascending — 1.00 is the worst),
 * then by distance.
 */
export function rankByConcern(trips: readonly TripTracking[]): TripTracking[] {
  const rank: Record<TrackingVerdict, number> = {
    gapped_unpaid: 0,
    gapped_free: 1,
    healthy: 2,
    not_measured: 3,
  };
  return [...trips].sort((a, b) => {
    const byVerdict = rank[trackingVerdict(a)] - rank[trackingVerdict(b)];
    if (byVerdict !== 0) return byVerdict;
    const ra = trackToChordRatio(a) ?? Number.POSITIVE_INFINITY;
    const rb = trackToChordRatio(b) ?? Number.POSITIVE_INFINITY;
    if (ra !== rb) return ra - rb;
    return b.totalKm - a.totalKm;
  });
}

/** Maps a raw row. `numeric` crosses the wire as a string. */
export function toTripTracking(row: Record<string, unknown>): TripTracking {
  const staff = row.staff as { full_name?: string } | null;
  const maxGap = row.max_gap_seconds as number | null;
  const gaps = row.tracking_gaps as number | null;
  const chord = row.straight_line_km;
  return {
    id: row.id as string,
    riderName: staff?.full_name ?? null,
    endedAt: (row.ended_at as string | null) ?? null,
    totalKm: Number(row.total_km ?? 0),
    straightLineKm: chord === null || chord === undefined ? null : Number(chord),
    trackingGaps: gaps ?? 0,
    maxGapSeconds: maxGap ?? 0,
    measured: isMeasured({ max_gap_seconds: maxGap, tracking_gaps: gaps }),
  };
}
